import { useEffect, useState } from "react";
import { Link } from "react-router";
import { authFetch } from "../../lib/auth-fetch";

interface SocialSettings {
	bluesky: { handle: string } | null;
}

type SaveState = "idle" | "saving" | "saved";

const Settings = () => {
	// Profile
	const [name, setName] = useState("");
	const [bio, setBio] = useState("");
	const [bannerColor, setBannerColor] = useState("#1a1a2e");
	const [nameSaveState, setNameSaveState] = useState<SaveState>("idle");

	// Social
	const [social, setSocial] = useState<SocialSettings | null>(null);
	const [socialLoading, setSocialLoading] = useState(true);
	const [handleUser, setHandleUser] = useState("");
	const [handleDomain, setHandleDomain] = useState(".bsky.social");
	const [customDomain, setCustomDomain] = useState("");
	const [appPassword, setAppPassword] = useState("");
	const [connecting, setConnecting] = useState(false);
	const [disconnecting, setDisconnecting] = useState(false);
	const [socialError, setSocialError] = useState<string | null>(null);

	useEffect(() => {
		authFetch("/api/settings/profile")
			.then((r) => r.json())
			.then((data) => {
				setName(data.name ?? "");
				setBio(data.bio ?? "");
				if (data.bannerColor) setBannerColor(data.bannerColor);
			})
			.catch(() => {});

		authFetch("/api/settings/social")
			.then((r) => r.json())
			.then((data: SocialSettings) => {
				setSocial(data);
				setSocialLoading(false);
			})
			.catch(() => setSocialLoading(false));
	}, []);

	const handleSaveName = async (e: React.FormEvent) => {
		e.preventDefault();
		setNameSaveState("saving");
		try {
			await authFetch("/api/settings/profile", {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name, bio, bannerColor }),
			});
			setNameSaveState("saved");
			setTimeout(() => setNameSaveState("idle"), 2000);
		} catch {
			setNameSaveState("idle");
		}
	};

	const handleConnect = async (e: React.FormEvent) => {
		e.preventDefault();
		setSocialError(null);
		setConnecting(true);

		try {
			const domain = handleDomain === "other"
				? "." + customDomain?.trim().replace(/^\.+/, "")
				: handleDomain;
			const fullHandle = `${handleUser.replace(/^@/, "")?.trim()}${domain}`;

			const res = await authFetch("/api/settings/social/bluesky", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ handle: fullHandle, appPassword }),
			});

			if (!res.ok) {
				const data = await res.json();
				setSocialError(data.error ?? "Failed to connect");
				return;
			}

			const data = await res.json();
			setSocial((prev) => ({ ...prev, bluesky: data.bluesky }));
			setHandleUser("");
			setHandleDomain(".bsky.social");
			setCustomDomain("");
			setAppPassword("");
		} catch {
			setSocialError("Could not reach server");
		} finally {
			setConnecting(false);
		}
	};

	const handleDisconnect = async () => {
		setDisconnecting(true);
		try {
			await authFetch("/api/settings/social/bluesky", { method: "DELETE" });
			setSocial((prev) => ({ ...prev, bluesky: null }));
		} finally {
			setDisconnecting(false);
		}
	};

	return (
		<div>
			<p><Link to="/">← Back</Link></p>
			<h2>Settings</h2>

			<section>
				<h3>Profile</h3>
				<form onSubmit={handleSaveName}>
					<div>
						<label>
							Display name
							<input
								type="text"
								placeholder="Your name"
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</label>
						<small>Shown to viewers as "{name || "Your name"} is live blogging this match"</small>
					</div>
					<div>
						<label>
							Bio
							<textarea
								placeholder="Tell viewers a bit about yourself…"
								value={bio}
								onChange={(e) => setBio(e.target.value)}
								rows={3}
							/>
						</label>
					</div>
					<div>
						<label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							Profile banner colour
							<input
								type="color"
								value={bannerColor}
								onChange={(e) => setBannerColor(e.target.value)}
							/>
						</label>
					</div>
					<button type="submit" disabled={nameSaveState === "saving"}>
						{nameSaveState === "saving" ? "Saving…" : nameSaveState === "saved" ? "Saved!" : "Save"}
					</button>
				</form>
			</section>

			<section>
				<h3>Connected accounts</h3>

				<h4>Bluesky</h4>
				{socialLoading && <p>Loading…</p>}
				{!socialLoading && social?.bluesky ? (
					<div>
						<p>Connected as <strong>@{social.bluesky.handle}</strong></p>
						<button type="button" onClick={handleDisconnect} disabled={disconnecting}>
							{disconnecting ? "Disconnecting…" : "Disconnect"}
						</button>
					</div>
				) : (
					!socialLoading && (
						<form onSubmit={handleConnect}>
							<div>
								<label>Handle</label>
								<div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
									<input
										type="text"
										placeholder="yourname"
										value={handleUser}
										onChange={(e) => setHandleUser(e.target.value)}
										required
									/>
									<select value={handleDomain} onChange={(e) => setHandleDomain(e.target.value)}>
										<option value=".bsky.social">.bsky.social</option>
										<option value="other">Other…</option>
									</select>
								</div>
								{handleDomain === "other" && (
									<input
										type="text"
										placeholder="yourdomain.com"
										value={customDomain}
										onChange={(e) => setCustomDomain(e.target.value)}
										required
									/>
								)}
							</div>
							<div>
								<label>
									App password
									<input
										type="password"
										placeholder="xxxx-xxxx-xxxx-xxxx"
										value={appPassword}
										onChange={(e) => setAppPassword(e.target.value)}
										required
									/>
								</label>
							</div>
							<small>
								Generate an app password at{" "}
								<a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noreferrer">
									bsky.app/settings/app-passwords
								</a>{" "}
								— don't use your main password.
							</small>
							{socialError && <p>{socialError}</p>}
							<button
								type="submit"
								disabled={connecting || !handleUser?.trim() || (handleDomain === "other" && !customDomain?.trim())}
							>
								{connecting ? "Connecting…" : "Connect"}
							</button>
						</form>
					)
				)}

				<h4>X / Twitter</h4>
				<p>Coming soon</p>
			</section>
		</div>
	);
};

export default Settings;
